import {
  CaretRightOutlined,
  DislikeFilled,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InfoCircleFilled,
  InfoCircleTwoTone,
  LeftOutlined,
  LikeFilled,
  PauseOutlined,
  RightOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Collapse, Radio, Result, Row, Space, Statistic, Tooltip, Typography, message } from 'antd';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import Paragraph from 'antd/lib/typography/Paragraph';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';
import { useAnswerTodayQuizMutation, useGetHistoryLksbGroupedQuery, useGetTodayLksbGroupedQuery, useGetTodayQuizQuery } from './homeAPI';
import { AnswerTodayQuiz } from './types';

const GROUP_ORDER = ['P', 'E', 'A', 'R', 'L', 'S'];

const { Text } = Typography;

function Home() {
  const containerRef = useRef<HTMLDivElement>(null);
  const originalLayoutStylesRef = useRef<{
    sidebarDisplay: string;
    contentMarginLeft: string;
    contentBackground: string;
  } | null>(null);
  const [tabActiveKey, setTabActiveKey] = useState<string | string[]>('0');
  const [quizAnswer, setQuizAnswer] = useState(-1);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [isAutoRotateFrozen, setIsAutoRotateFrozen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [answerTodayQuizResponse, setAnswerTodayQuizResponse] = useState<AnswerTodayQuiz | undefined>();
  const { data, error, isFetching } = useGetTodayLksbGroupedQuery();
  const { data: historyData, error: historyError, isFetching: historyFetching } = useGetHistoryLksbGroupedQuery();
  const { data: quizData, error: quizError, isFetching: quizFetching } = useGetTodayQuizQuery();
  const [answerTodayQuiz] = useAnswerTodayQuizMutation();

  useEffect(() => {
    if (quizData != null && quizData != undefined && !quizData.answered) {
      setTabActiveKey('1');
    }
  }, [quizData]);

  useEffect(() => {
    return () => {
      setAnswerTodayQuizResponse(undefined);
    };
  }, []);

  useEffect(() => {
    if (isAutoRotateFrozen) {
      return;
    }

    const interval = setInterval(() => {
      setActiveGroupIndex((prev) => (prev + 1) % GROUP_ORDER.length);
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAutoRotateFrozen]);

  useEffect(() => {
    const sidebarRow = document.querySelector('.ant-layout-header .ant-row') as HTMLElement | null;
    const contentWrapper = containerRef.current?.parentElement as HTMLElement | null;

    if (!sidebarRow || !contentWrapper) return;

    if (isFullscreen) {
      if (!originalLayoutStylesRef.current) {
        originalLayoutStylesRef.current = {
          sidebarDisplay: sidebarRow.style.display,
          contentMarginLeft: contentWrapper.style.marginLeft,
          contentBackground: contentWrapper.style.background,
        };
      }

      sidebarRow.style.display = 'none';
      contentWrapper.style.marginLeft = '0px';
      contentWrapper.style.background = '#f0f2f5';
      return;
    }

    if (originalLayoutStylesRef.current) {
      sidebarRow.style.display = originalLayoutStylesRef.current.sidebarDisplay;
      contentWrapper.style.marginLeft = originalLayoutStylesRef.current.contentMarginLeft;
      contentWrapper.style.background = originalLayoutStylesRef.current.contentBackground;
      originalLayoutStylesRef.current = null;
    }
  }, [isFullscreen]);

  useEffect(() => {
    return () => {
      const sidebarRow = document.querySelector('.ant-layout-header .ant-row') as HTMLElement | null;
      const contentWrapper = containerRef.current?.parentElement as HTMLElement | null;

      if (!sidebarRow || !contentWrapper || !originalLayoutStylesRef.current) return;

      sidebarRow.style.display = originalLayoutStylesRef.current.sidebarDisplay;
      contentWrapper.style.marginLeft = originalLayoutStylesRef.current.contentMarginLeft;
      contentWrapper.style.background = originalLayoutStylesRef.current.contentBackground;
      originalLayoutStylesRef.current = null;
    };
  }, []);

  const options: any = {
    scales: {
      y: { beginAtZero: true },
    },
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      title: {
        display: false,
      },
    },
    datasets: {
      line: {
        pointRadius: 0, // disable for all `'line'` datasets
      },
    },
  };

  const formatMetricAmount = (value: number) => {
    if (value == null || value == undefined || Number.isNaN(+value)) {
      return '-';
    }

    return new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(+value);
  };

  const getMetricColor = (isGood: boolean | null | undefined) => {
    if (isGood == null || isGood === undefined) {
      return '#bfbfbf'; // gray
    }
    if (isGood == true) {
      return '#3f8600'; // green
    } else {
      return '#cf1322'; // red
    }
  };

  const activeGroup = GROUP_ORDER[activeGroupIndex];
  const activeTodayPage = data?.pages?.find((page) => page.group === activeGroup);
  const activeHistoryPage = historyData?.pages?.find((page) => page.group === activeGroup);
  const summaryHeight = isFullscreen ? '34vh' : '30vh';
  const chartHeight = isFullscreen ? '43vh' : '36vh';

  const getQuizCompleteSubtitle = () => {
    if (quizData == undefined || quizData == null) {
      return '';
    }
    return `Nilai anda per-bulan ini adalah ${quizData?.monthlyScore} dari ${quizData?.monthlyScoreBase}`;
  };

  const answerQuiz = async () => {
    const hide = message.loading('Loading', 0);
    if (quizAnswer == null || quizAnswer == undefined || quizAnswer < 0) {
      message.error('Anda belum menjawab!');
    }
    answerTodayQuiz(quizAnswer)
      .unwrap()
      .then((e) => {
        hide();
        setAnswerTodayQuizResponse(e);
        message.success('Berhasil menjawab quiz!');
      })
      .catch(() => {
        hide();
        message.error('Gagal menjawab quiz!');
      });
  };

  const renderAnswerResult = () => {
    if (answerTodayQuizResponse) {
      if (answerTodayQuizResponse.isCorrect) {
        return <Result status="success" title="Jawaban Anda Benar!" subTitle={getQuizCompleteSubtitle()} />;
      }
      let correctAnswer = answerTodayQuizResponse.correctAnswer
        .map((e) => {
          return quizData?.answers[e];
        })
        .map((e, idx) => `(${idx + 1}) ${e?.text}.` || '')
        .join(' ');
      return <Result status="error" title="Jawaban Anda Salah!" subTitle={`Jawaban benar: ${correctAnswer}`} />;
    }
    return <></>;
  };

  const shouldShowNoQuestion = () => {
    return quizData && !answerTodayQuizResponse ? quizData.answered : false;
  };

  const toPrevGroup = () => {
    setActiveGroupIndex((prev) => (prev - 1 + GROUP_ORDER.length) % GROUP_ORDER.length);
  };

  const toNextGroup = () => {
    setActiveGroupIndex((prev) => (prev + 1) % GROUP_ORDER.length);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const toggleAutoRotateFreeze = () => {
    setIsAutoRotateFrozen((prev) => !prev);
  };

  return (
    <div ref={containerRef} style={{ minHeight: isFullscreen ? 'calc(100vh - 64px)' : undefined, background: '#f0f2f5' }}>
      {!isFullscreen && (
        <Collapse bordered={false} activeKey={tabActiveKey} onChange={(e) => setTabActiveKey(e)}>
          <CollapsePanel header="Quiz" key={1}>
            <ContentWithErrorHandler isError={quizError} isLoading={quizFetching} withBox={false} fullScreen={false} show={!quizData?.answered}>
              <Paragraph>{quizData?.question}</Paragraph>
              <div style={{ marginBottom: 16 }}>
                <Radio.Group
                  value={quizAnswer}
                  onChange={(e) => {
                    setQuizAnswer(e.target.value);
                    console.log(e);
                  }}
                >
                  <Space direction="vertical">
                    {quizData?.answers.map((e, idx) => (
                      <Radio value={idx}>{e.text}</Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>
              <Button type="primary" onClick={answerQuiz}>
                Jawab
              </Button>
            </ContentWithErrorHandler>
            <ContentWithErrorHandler
              isError={false}
              isLoading={false}
              withBox={false}
              fullScreen={false}
              show={answerTodayQuizResponse ? true : false}
            >
              {renderAnswerResult()}
            </ContentWithErrorHandler>
            <ContentWithErrorHandler isError={false} isLoading={false} withBox={false} fullScreen={false} show={shouldShowNoQuestion()}>
              <Result icon={<SmileOutlined />} title="Quiz hari ini sudah terjawab" subTitle={getQuizCompleteSubtitle()} />
            </ContentWithErrorHandler>
          </CollapsePanel>
        </Collapse>
      )}
      <Card
        size="small"
        title="Ringkasan hari ini (semua nilai dalam persentase)"
        bordered={false}
        style={{ marginTop: 8 }}
        bodyStyle={{ padding: 10, height: summaryHeight, overflow: 'hidden' }}
      >
        <ContentWithErrorHandler isError={error} isLoading={isFetching} withBox={false} fullScreen={false}>
          <Row justify="space-between" style={{ marginBottom: 8 }}>
            <Col>
              <Text strong>{`Group ${activeGroup}`}</Text>
            </Col>
            <Col>
              <Space size={6}>
                <Button size="small" icon={<LeftOutlined />} onClick={toPrevGroup}>
                  Prev
                </Button>
                <Button size="small" icon={<RightOutlined />} onClick={toNextGroup}>
                  Next
                </Button>
                <Button size="small" icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen}>
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
                <Button size="small" icon={isAutoRotateFrozen ? <CaretRightOutlined /> : <PauseOutlined />} onClick={toggleAutoRotateFreeze}>
                  {isAutoRotateFrozen ? 'Unfreeze Carousel' : 'Freeze Carousel'}
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isAutoRotateFrozen ? 'Carousel dibekukan' : 'Carousel menyala'}
                </Text>
              </Space>
            </Col>
          </Row>
          <div style={{ height: 'calc(100% - 38px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
            <Row gutter={[8, 8]}>
              {(activeTodayPage?.items || []).map((item) => (
                <Col xs={24} sm={12} md={8} lg={6} key={item.key}>
                  <Card size="small" bordered bodyStyle={{ padding: 8 }}>
                    <Statistic
                      title={
                        <>
                          <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '16px' }}>{item.code}</div>
                          <div
                            style={{
                              color: '#6c757d',
                              marginTop: 2,
                              marginBottom: 0,
                              padding: 0,
                              fontSize: 11,
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.name}
                          </div>
                        </>
                      }
                      value={+item.amount}
                      prefix={
                        item.isGood == null || item.isGood === undefined ? (
                          <Tooltip title="Data konfigurasi kriteria good metric untuk metric ini tidak ditemukan, sehingga status good/bad tidak dapat ditentukan. Tambahkan di menu 'Pengaturan Umum'">
                            <InfoCircleTwoTone twoToneColor="orange" style={{ fontSize: 20, marginRight: 4 }} />
                          </Tooltip>
                        ) : item.isGood === true ? (
                          <LikeFilled style={{ color: '#3f8600', fontSize: 20, marginRight: 4 }} />
                        ) : (
                          <DislikeFilled style={{ color: '#cf1322', fontSize: 20, marginRight: 4 }} />
                        )
                      }
                      formatter={(value) => formatMetricAmount(Number(value))}
                      valueStyle={{ fontSize: 24, fontWeight: 700, lineHeight: '26px', color: getMetricColor(item.isGood) }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </ContentWithErrorHandler>
      </Card>
      <Card
        style={{ marginTop: 8 }}
        size="small"
        title={`Riwayat Group ${activeGroup} (12 Bulan Terakhir)`}
        bodyStyle={{ padding: 10, paddingTop: 0, height: chartHeight }}
        bordered={false}
      >
        <ContentWithErrorHandler isError={historyError} isLoading={historyFetching} withBox={false} fullScreen={false}>
          {activeHistoryPage ? <Line data={{ labels: activeHistoryPage.labels, datasets: activeHistoryPage.datasets }} options={options} /> : <></>}
        </ContentWithErrorHandler>
      </Card>
    </div>
  );
}

export default Home;
